import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Card, Spinner } from 'react-bootstrap';
import { getBrands, getModelsByBrand, getBodyTypes } from '../../services/vehicleDataService';
/**
 * Araç formlarında kullanılan Temel Bilgiler formu
 * 
 * @param {Object} props.data - Form verisi (formData veya vehicle)
 * @param {Function} props.handleChange - Input değişikliklerini yakalayan fonksiyon
 * @param {boolean} props.isEditMode - Düzenleme modu aktif mi? (opsiyonel)
 */
const VehicleBasicInfoForm = ({ data, handleChange }) => {
    const [brands, setBrands] = useState([]);
    const [models, setModels] = useState([]);
    const [bodyTypes, setBodyTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modelLoading, setModelLoading] = useState(false);

    // Fetch brands on component mount
    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const response = await getBrands();
                if (response.success) {
                    setBrands(response.data);
                }
            } catch (error) {
                console.error('Error fetching brands:', error);
            } finally {
                setLoading(false);
            }
        };

        const fetchBodyTypes = async () => {
            try {
                const response = await getBodyTypes();
                if (response.success) {
                    setBodyTypes(response.data);
                }
            } catch (error) {
                console.error('Error fetching body types:', error);
            }
        };

        fetchBrands();
        fetchBodyTypes();
    }, []);

    // Fetch models when brand changes
    useEffect(() => {
        const fetchModels = async () => {
            if (!data.brand) {
                setModels([]);
                return;
            }

            try {
                setModelLoading(true);
                const response = await getModelsByBrand(data.brand);
                if (response.success) {
                    setModels(response.data);
                }
            } catch (error) {
                console.error(`Error fetching models for brand ${data.brand}:`, error);
                setModels([]);
            } finally {
                setModelLoading(false);
            }
        };

        fetchModels();
    }, [data.brand]);

    // Create a custom handleModelChange to update bodyType automatically
    const handleModelChange = (e) => {
        const selectedModelName = e.target.value;
        handleChange(e);

        // Also set the bodyType if available
        const selectedModel = models.find(model => model.name === selectedModelName);
        if (selectedModel && selectedModel.type) {
            // Create a synthetic event to update the bodyType
            const syntheticEvent = {
                target: {
                    name: 'bodyType',
                    value: selectedModel.type
                }
            };
            handleChange(syntheticEvent);
        }
    };

    return (
        <Card className="mb-4">
            <Card.Header>Temel Bilgiler</Card.Header>
            <Card.Body>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="title">
                            <Form.Label>İlan Başlığı</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                value={data.title || ''}
                                onChange={handleChange}
                                placeholder="Örn: Sahibinden Temiz Honda Civic"
                                required
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group className="mb-3" controlId="price">
                            <Form.Label>Fiyat (₺)</Form.Label>
                            <Form.Control
                                type="number"
                                name="price"
                                value={data.price || ''}
                                onChange={handleChange}
                                placeholder="Araç fiyatı"
                                required
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="brand">
                            <Form.Label>Marka</Form.Label>
                            {loading ? (
                                <div className="text-center">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <Form.Select
                                    name="brand"
                                    value={data.brand || ''}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">Marka Seçin</option>
                                    {brands.map((brand) => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="model">
                            <Form.Label>Model</Form.Label>
                            {modelLoading ? (
                                <div className="text-center">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : (
                                <Form.Select
                                    name="model"
                                    value={data.model || ''}
                                    onChange={handleModelChange}
                                    disabled={!data.brand || loading}
                                    required
                                >
                                    <option value="">Model Seçin</option>
                                    {models.map((model) => (
                                        <option key={model.name} value={model.name}>
                                            {model.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            )}
                        </Form.Group>
                    </Col>

                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="bodyType">
                            <Form.Label>Kasa Tipi</Form.Label>
                            <Form.Select
                                name="bodyType"
                                value={data.bodyType || ''}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Kasa Tipi Seçin</option>
                                {bodyTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {type}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={4}>
                        <Form.Group className="mb-3" controlId="year">
                            <Form.Label>Yıl</Form.Label>
                            <Form.Control
                                type="number"
                                name="year"
                                value={data.year || ''}
                                onChange={handleChange}
                                placeholder="Örn: 2020"
                                min="1900"
                                max={new Date().getFullYear()}
                                required
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

export default VehicleBasicInfoForm;